/**
 * Flag Gallery Component
 * Displays a grid of selectable country flag options for number plate badges
 * Adapted from prototype for Shopify theme integration
 */

class BundleFlagGallery {
  constructor(config = {}) {
    this.flags = config.flags || this.getDefaultFlags();
    this.selectedId = config.selectedId || null;
    this.showUpload = config.showUpload !== false;
    this.uploadPrice = config.uploadPrice || 4.99;
    this.currency = config.currency || '+£';
    this.searchTerm = '';
  }

  /**
   * Get default flag options
   */
  getDefaultFlags() {
    return [
      { id: 'gb', name: 'Great Britain', icon: 'gb', price: 0 },
      { id: 'england', name: 'England', icon: 'england', price: 0 },
      { id: 'scotland', name: 'Scotland', icon: 'scotland', price: 0 },
      { id: 'wales', name: 'Wales', icon: 'wales', price: 0 },
      { id: 'eu', name: 'European Union', icon: 'eu', price: 0 }
    ];
  }

  /**
   * Render the flag gallery
   */
  render() {
    const filteredFlags = this.filterFlags();

    return `
      <div class="bundle-flag-gallery">
        ${this.renderSearch()}
        <div class="bundle-flag-gallery__grid">
          ${filteredFlags.map(flag => this.renderFlagCard(flag)).join('')}
          ${this.showUpload ? this.renderUploadCard() : ''}
        </div>
      </div>
    `;
  }

  /**
   * Render search bar
   */
  renderSearch() {
    return `
      <div class="bundle-flag-gallery__search">
        <div class="bundle-flag-gallery__search-wrapper">
          ${this.renderSearchIcon()}
          <input
            type="text"
            class="bundle-flag-gallery__search-input"
            placeholder="Search for a flag..."
            data-action="search-flags"
            value="${this.searchTerm}"
          />
        </div>
      </div>
    `;
  }

  /**
   * Render search icon
   */
  renderSearchIcon() {
    return `
      <svg class="bundle-flag-gallery__search-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <circle cx="11" cy="11" r="8" />
        <path d="m21 21-4.35-4.35" />
      </svg>
    `;
  }

  /**
   * Filter flags based on search term
   */
  filterFlags() {
    if (!this.searchTerm) {
      return this.flags;
    }

    const term = this.searchTerm.toLowerCase();
    return this.flags.filter(flag =>
      flag.name.toLowerCase().includes(term)
    );
  }

  /**
   * Render individual flag card
   */
  renderFlagCard(flag) {
    const isSelected = flag.id === this.selectedId;
    const cardClasses = [
      'bundle-flag-gallery__item',
      isSelected ? 'bundle-flag-gallery__item--selected' : ''
    ].filter(Boolean).join(' ');

    return `
      <div
        class="${cardClasses}"
        data-action="select-flag"
        data-flag-id="${flag.id}"
      >
        ${isSelected ? this.renderCheckmark() : ''}
        <div class="bundle-flag-gallery__icon">
          ${this.renderFlagIcon(flag.icon)}
        </div>
        <div class="bundle-flag-gallery__name">${flag.name}</div>
        ${flag.price > 0 ? this.renderPrice(flag.price) : ''}
      </div>
    `;
  }

  /**
   * Render upload image card
   */
  renderUploadCard() {
    const isSelected = this.selectedId === 'upload';
    const cardClasses = [
      'bundle-flag-gallery__item',
      'bundle-flag-gallery__item--upload',
      isSelected ? 'bundle-flag-gallery__item--selected' : ''
    ].filter(Boolean).join(' ');

    return `
      <div
        class="${cardClasses}"
        data-action="upload-image"
        data-flag-id="upload"
      >
        ${isSelected ? this.renderCheckmark() : ''}
        <div class="bundle-flag-gallery__icon bundle-flag-gallery__icon--upload">
          ${this.renderUploadIcon()}
        </div>
        <div class="bundle-flag-gallery__name">Upload Image</div>
        ${this.renderPrice(this.uploadPrice)}
      </div>
    `;
  }

  /**
   * Render checkmark for selected state
   */
  renderCheckmark() {
    return `
      <div class="bundle-flag-gallery__checkmark">
        <svg viewBox="0 0 24 24" fill="currentColor">
          <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z"/>
        </svg>
      </div>
    `;
  }

  /**
   * Render price display
   */
  renderPrice(price) {
    return `
      <div class="bundle-flag-gallery__price">
        ${this.currency}${price.toFixed(2)}
      </div>
    `;
  }

  /**
   * Render flag icon based on type
   */
  renderFlagIcon(iconType) {
    const icons = {
      gb: `
        <svg viewBox="0 0 60 60" fill="none">
          <circle cx="30" cy="30" r="30" fill="#012169"/>
          <path d="M30 0 L60 30 L30 60 L0 30 Z" fill="#fff" stroke="#C8102E" stroke-width="6"/>
          <path d="M30 0 L60 30 M30 0 L0 30 M30 60 L60 30 M30 60 L0 30" stroke="#fff" stroke-width="10"/>
          <path d="M30 0 L60 30 M30 0 L0 30 M30 60 L60 30 M30 60 L0 30" stroke="#C8102E" stroke-width="6"/>
          <path d="M0 30 L60 30 M30 0 L30 60" stroke="#fff" stroke-width="16"/>
          <path d="M0 30 L60 30 M30 0 L30 60" stroke="#C8102E" stroke-width="10"/>
        </svg>
      `,
      england: `
        <svg viewBox="0 0 60 60" fill="none">
          <circle cx="30" cy="30" r="30" fill="#fff"/>
          <rect x="24" y="0" width="12" height="60" fill="#CE1124"/>
          <rect x="0" y="24" width="60" height="12" fill="#CE1124"/>
        </svg>
      `,
      scotland: `
        <svg viewBox="0 0 60 60" fill="none">
          <circle cx="30" cy="30" r="30" fill="#0065BD"/>
          <path d="M10 10 L50 50 M50 10 L10 50" stroke="#fff" stroke-width="8"/>
        </svg>
      `,
      wales: `
        <svg viewBox="0 0 60 60" fill="none">
          <circle cx="30" cy="30" r="30" fill="#fff"/>
          <rect x="0" y="30" width="60" height="30" fill="#00A651"/>
          <path d="M25 25 Q30 20 35 25 L33 35 Q30 32 27 35 Z" fill="#C8102E"/>
        </svg>
      `,
      eu: `
        <svg viewBox="0 0 60 60" fill="none">
          <circle cx="30" cy="30" r="30" fill="#003399"/>
          <g fill="#FFCC00">
            ${this.renderEuStars()}
          </g>
        </svg>
      `
    };

    return icons[iconType] || this.renderDefaultFlag();
  }

  /**
   * Render EU stars in circle
   */
  renderEuStars() {
    const stars = [];
    const centerX = 30;
    const centerY = 30;
    const radius = 15;

    for (let i = 0; i < 12; i++) {
      const angle = (i * 30 - 90) * (Math.PI / 180);
      const x = centerX + radius * Math.cos(angle);
      const y = centerY + radius * Math.sin(angle);
      stars.push(`
        <path d="M${x} ${y-3} L${x+0.9} ${y-0.9} L${x+3} ${y} L${x+0.9} ${y+0.9} L${x} ${y+3} L${x-0.9} ${y+0.9} L${x-3} ${y} L${x-0.9} ${y-0.9} Z"/>
      `);
    }

    return stars.join('');
  }

  /**
   * Render default flag icon
   */
  renderDefaultFlag() {
    return `
      <svg viewBox="0 0 60 60" fill="none">
        <circle cx="30" cy="30" r="30" fill="#E5E7EB"/>
        <rect x="15" y="15" width="30" height="20" rx="2" stroke="#6B7280" stroke-width="2" fill="none"/>
      </svg>
    `;
  }

  /**
   * Render upload icon
   */
  renderUploadIcon() {
    return `
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
        <polyline points="17 8 12 3 7 8"/>
        <line x1="12" y1="3" x2="12" y2="15"/>
      </svg>
    `;
  }

  /**
   * Update search term
   */
  setSearchTerm(term) {
    this.searchTerm = term;
  }

  /**
   * Update selected flag
   */
  setSelectedId(id) {
    this.selectedId = id;
  }

  /**
   * Get currently selected flag data
   */
  getSelectedFlag() {
    if (this.selectedId === 'upload') {
      return {
        id: 'upload',
        name: 'Upload Image',
        price: this.uploadPrice
      };
    }

    return this.flags.find(flag => flag.id === this.selectedId) || null;
  }

  /**
   * Static method to render with config
   */
  static create(config) {
    return new BundleFlagGallery(config).render();
  }
}

// Export to global scope
window.BundleFlagGallery = BundleFlagGallery;
